import type { AssuranceSort, AssuranceVideo, SafetyVideoCategories } from "@unbox/api";
import {
  Announcement03,
  DotsVertical,
  FilePlus02,
  FilterLines,
  MessageTextSquare02,
  SearchMd,
  ShieldPlus,
} from "@untitledui/icons";
import { Camera } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  useAssuranceVideoRowsFetcher,
  useAssuranceVideos,
  useAssuranceVideosDownload,
} from "@/api/assurance/assurance-videos";
import { useCreator } from "@/api/creator";
import { usePagination } from "@/api/pagination";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { SelectAllPagesButton } from "@/components/application/table/select-all-pages-button";
import { Table, TableCard } from "@/components/application/table/table";
import { UnboxTableEmptyStateContent } from "@/components/application/table/unbox-table";
import { LogActionDropdown, TakeActionDropdown } from "@/components/assurance/action-dropdown";
import { ActionRecordDialog } from "@/components/assurance/action-record-dialog";
import { AddRiskModal } from "@/components/assurance/add-risk-bulk-modal";
import { AddRiskVideoModal } from "@/components/assurance/add-risk-video-modal";
import { AdThumbnailBadge } from "@/components/assurance/badges/ad-campaign-badge";
import { FlagBadges } from "@/components/assurance/badges/flag-badge";
import { DetailedVideoStatusBadge } from "@/components/assurance/badges/video-status-badge";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { type ReviewRiskAction, ReviewRiskDropdown } from "@/components/assurance/review-risk-dropdown";
import { ReviewRiskModal } from "@/components/assurance/review-risk-modal";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { FilterPills } from "@/components/filters/filter-pills";
import { VideoFilterPanel } from "@/components/filters/panels";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { Dialog } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { VideoDetails } from "@/components/video-details";
import { generateFilterSyncConfig, useAutoFilterUrlSync } from "@/hooks/use-auto-filter-sync";
import { useCache } from "@/hooks/use-cache";
import { useCrossPageSelection } from "@/hooks/use-cross-page-selection";
import { useDebounce } from "@/hooks/use-debounce";
import { useSearchUrlSync } from "@/hooks/use-search-url-sync";
import { useSelectAllAcrossPages } from "@/hooks/use-select-all-across-pages";
import { useSort } from "@/hooks/use-sort";
import { useUrlFilterSync } from "@/hooks/use-url-filter-sync";
import { type ActionType, getLogActionLabel } from "@/lib/assurance/action-types";
import { REVIEW_TABLE_EMPTY_STATES } from "@/pages/assurance/utils";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import type { FilterField, FiltersFormValues } from "@/providers/filter-registry";
import { useMerchant } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";
import { downloadCSV } from "@/utils/download-csv";
import { formatCreatorId } from "@/utils/formatters";
import { pluralize } from "@/utils/text";

const videoFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
});
const isMoreThanDayAgo = (date: Date): boolean => {
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return date < oneDayAgo;
};

type TimeLastContactedTag = "<24h" | ">24h" | ">48h" | "Never";

const getTimeLastContactedTag = (contactedAt: Date | string | null | undefined): TimeLastContactedTag => {
  if (!contactedAt) return "Never";
  const date = typeof contactedAt === "string" ? new Date(contactedAt) : contactedAt;
  const now = new Date();
  const hoursAgo = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (hoursAgo < 24) return "<24h";
  if (hoursAgo < 48) return ">24h";
  return ">48h";
};

const TimeLastContactedBadge = ({ contactedAt }: { contactedAt: Date | string | null | undefined }) => {
  const tag = getTimeLastContactedTag(contactedAt);
  const styles: Record<TimeLastContactedTag, string> = {
    "<24h": "bg-green-100 text-green-700",
    ">24h": "bg-yellow-100 text-yellow-700",
    ">48h": "bg-red-100 text-red-700",
    Never: "bg-gray-100 text-gray-600",
  };
  return <span className={cx("px-2 py-1 rounded-full text-xs font-medium", styles[tag])}>{tag}</span>;
};

type VideoData = {
  videoId: string;
  creatorId: string | null;
  thumbnail: string | null;
  productList?: string[];
};

type SafetyVideosTableInnerProps = {
  category: SafetyVideoCategories;
  title: ReactNode;
  tabs: ReactNode;
  initialVideoId?: string;
  onVideoIdChange?: (videoId: string | undefined) => void;
  navigate?: (options: {
    search: (prev: Record<string, unknown>) => Record<string, unknown>;
    replace?: boolean;
  }) => void;
  currentSearch?: Partial<FiltersFormValues> & Record<string, unknown>;
};

const SafetyVideosTableInner = ({
  category,
  title,
  tabs,
  initialVideoId,
  onVideoIdChange,
  navigate,
  currentSearch,
}: SafetyVideosTableInnerProps) => {
  const [selectedVideoId, setSelectedVideoId] = useState<string | undefined>(initialVideoId);

  const merchantId = useMerchant();

  const handleVideoIdChange = (videoId: string | undefined) => {
    setSelectedVideoId(videoId);
    onVideoIdChange?.(videoId);
  };

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [preselectedAction, setPreselectedAction] = useState<ActionType | undefined>();
  const [preselectedCategory, setPreselectedCategory] = useState<"take" | "log" | undefined>();

  const [addRiskVideoId, setAddRiskVideoId] = useState<string>();
  const [rowInviteCreatorId, setRowInviteCreatorId] = useState<string>();
  const [addToListsCreatorId, setAddToListsCreatorId] = useState<string>();
  const [rowMessageCreatorId, setRowMessageCreatorId] = useState<string>();

  // Review Risk modal state
  const [reviewRiskModalOpen, setReviewRiskModalOpen] = useState(false);
  const [reviewRiskAction, setReviewRiskAction] = useState<ReviewRiskAction | undefined>();

  const pagination = usePagination();
  const { sort, descriptor, setFromDescriptor } = useSort<AssuranceSort>("publishDate");

  const { apiFilters, form, activeFields, reset, hasActiveFilters: hasActiveFilterFields } = useFilters();

  // Automatically sync URL params to form based on active fields
  useAutoFilterUrlSync(form, currentSearch || {}, activeFields);

  // Generate field mapping and currentSearch dynamically
  const { fieldMapping, currentSearch: filterSearch } = useMemo(
    () => generateFilterSyncConfig(activeFields, currentSearch || {}),
    [activeFields, currentSearch],
  );

  // Sync form changes back to URL (if navigate is provided)
  useUrlFilterSync({
    form,
    fieldMapping,
    navigate: navigate || (() => {}),
    currentSearch: filterSearch,
  });

  const [search, setSearch] = useState((currentSearch?.search as string) || "");
  const debouncedSearch = useDebounce(search);
  const filters = { ...apiFilters, search: debouncedSearch || "" };

  // Sync search to URL (if navigate is provided)
  useSearchUrlSync({
    search,
    setSearch,
    navigate: navigate || (() => {}),
    currentSearch: { search: (currentSearch?.search as string) || "" },
  });
  const { data, isLoading } = useAssuranceVideos({
    category,
    pagination: pagination.pagination,
    sort,
    ...filters,
  });
  const { mutateAsync, isPending: isDownloading } = useAssuranceVideosDownload();
  const total = useCache(data?.total);

  const hasActiveFilters = hasActiveFilterFields || !!debouncedSearch;

  const handleClearFilters = useCallback(() => {
    reset();
    setSearch("");
    pagination.setPage(1);
  }, [reset, pagination]);

  const {
    selectedKeys: selected,
    selectedData: selectedVideosData,
    isAllPagesSelected,
    selectedCount,
    clear: clearSelection,
    selectAllAcrossPages,
    createSelectionHandler,
  } = useCrossPageSelection<AssuranceVideo, VideoData>({
    getKey: useCallback((v: AssuranceVideo) => v.videoId, []),
    toData: useCallback(
      (v: AssuranceVideo) => ({
        videoId: v.videoId,
        creatorId: v.creatorId,
        thumbnail: v.urlFirstFrame,
        productList: v.productList,
      }),
      [],
    ),
  });

  const handleSelectionChange = useMemo(
    () => createSelectionHandler(data?.videos),
    [createSelectionHandler, data?.videos],
  );

  const fetchChunk = useAssuranceVideoRowsFetcher({ category, sort, ...filters });
  const selectAll = useSelectAllAcrossPages({
    fetchChunk,
    total: total ?? 0,
    loadedCount: data?.videos.length ?? 0,
    selection: { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection },
    resetKey: JSON.stringify({ category, sort, filters }),
  });
  const handleClearSelection = selectAll.clear;

  const downloadTableData = async () => {
    if (!total) return;
    const rows = await mutateAsync({
      total,
      category,
      sort,
      ...filters,
    });

    // Calculate max number of products across all rows
    const maxProducts = Math.max(...rows.map((r) => r.productIds?.length ?? 0), 0);

    // Create dynamic product columns
    const productCols = Array.from({ length: maxProducts }, (_, i) => `product${i + 1}` as const);
    const productHeaders = Array.from({ length: maxProducts }, (_, i) => `Product ${i + 1} ID`);

    // Transform rows to include individual product columns
    const transformedRows = rows.map((r) => {
      const productFields: Record<string, string> = {};
      for (let i = 0; i < maxProducts; i++) {
        productFields[`product${i + 1}`] = r.productIds?.[i]?.replace("PID-", "") ?? "";
      }
      return {
        ...r,
        ...productFields,
        creatorId: r.creatorId?.replace("CID-", "") ?? "",
        actionTypes: r.actionTypes?.map((a) => getLogActionLabel(a as ActionType)),
        flagTiers: r.flagTiers.map((t) => `T${t}`),
        creatorContactedAt: r.creatorContactedAt
          ? isMoreThanDayAgo(new Date(r.creatorContactedAt))
            ? "Yes"
            : "No"
          : "N/A",
      };
    });

    downloadCSV(
      transformedRows,
      category,
      [
        "videoId",
        "videoTitle",
        "creatorId",
        "publishDate",
        "flagTypes",
        "flagTiers",
        "actionTypes",
        "creatorContactedAt",
        "videoUrl",
        ...productCols,
      ] as (keyof (typeof transformedRows)[number])[],
      [
        "Video ID",
        "Video Title",
        "Creator",
        "Published Date",
        "Risk Types",
        "Risk Tiers",
        "Actions Taken",
        "Creator Contacted >24h Ago",
        "Video URL",
        ...productHeaders,
      ],
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to reset filters when category changes, not when availableFilters changes
  useEffect(() => {
    const allFilters: FilterField[] = ["riskTier", "riskId", "actionTaken"];
    const filtersToReset: string[] = [];

    for (const filter of allFilters) {
      if (!availableFilters[category].includes(filter)) {
        form.resetField(filter);
        filtersToReset.push(filter);
      }
    }

    // Also clear inapplicable filters from URL
    if (filtersToReset.length > 0 && navigate) {
      navigate({
        search: (prev) => {
          const newSearch = { ...prev };
          for (const filter of filtersToReset) {
            delete newSearch[filter];
          }
          return newSearch;
        },
        replace: true,
      });
    }

    pagination.setPage(0);
  }, [category]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to reset page when filters change, not when pagination.setPage changes
  useEffect(() => {
    const subscription = form.watch(() => pagination.setPage(1));
    return subscription.unsubscribe;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to reset page when search changes
  useEffect(() => {
    pagination.setPage(1);
  }, [debouncedSearch]);

  const showLatestAction = ["actionInProgress", "loggedAsComplete"].includes(category);

  useEffect(() => {
    if (showLatestAction && sort.column === "status") {
      setFromDescriptor({ column: "publishDate", direction: "descending" });
    }
  }, [showLatestAction, sort.column]);

  const latestAction = (item: AssuranceVideo) => {
    const latest = item.actionTypes?.slice(-1)[0];
    if (!latest) return "N/A";
    return getLogActionLabel(latest as ActionType);
  };

  return (
    <div className="flex flex-1 flex-row gap-5 relative min-w-0 w-full">
      <div
        className={cx(
          "flex flex-1 flex-col gap-5 h-full transition-all duration-300 min-w-0 w-full",
          filtersOpen && "mr-96",
        )}
      >
        {/* Row 1: Title on left, Export button on right */}
        <div className="flex flex-row items-start justify-between flex-shrink-0">
          {title}
          <div className="flex flex-row gap-2">
            <ExportCsvButton onClick={() => downloadTableData()} isDownloading={isDownloading} label="Export as CSV" />
          </div>
        </div>

        {/* Row 2: Tabs on left, Search/Filters on right - OVERLAYABLE */}
        <div className="flex flex-row items-center justify-between flex-shrink-0 relative flex-wrap gap-4">
          {tabs}
          <div className="flex flex-row gap-4 items-center justify-between w-full">
            <span className="text-tertiary text-sm font-semibold pr-1">
              {isLoading ? "Loading..." : pluralize(total ?? 0, "result", "results")}
            </span>
            <div className="flex gap-2">
              <Input
                size="sm"
                inputClassName="text-sm"
                className="w-72"
                value={search}
                onChange={setSearch}
                placeholder="Search video title or creator..."
                icon={SearchMd}
              />
              <Button
                className="font-medium text-xs"
                color="secondary"
                onClick={() => setFiltersOpen((prev) => !prev)}
                iconLeading={<FilterLines className="w-4 h-4" />}
              >
                Filter
              </Button>
            </div>
          </div>

          {/* Selection bar overlay - covers entire row 2 */}
          {selected.length > 0 && (
            <div className="absolute inset-0 z-20 flex flex-row justify-between items-center rounded-lg bg-brand-primary -my-2 p-2">
              <div className="flex flex-row items-center gap-2">
                <CloseButton onClick={handleClearSelection} />
                <span className="text-sm font-medium">{selectedCount.toLocaleString()} selected</span>
                <SelectAllPagesButton state={selectAll} />
              </div>
              <div className="flex flex-row gap-2 items-center">
                <TakeActionDropdown
                  disabled={selectAll.isFetching}
                  onSelectAction={(actionType, category) => {
                    setPreselectedAction(actionType);
                    setPreselectedCategory(category);
                    setActionModalOpen(true);
                  }}
                />
                <LogActionDropdown
                  disabled={selectAll.isFetching}
                  onSelectAction={(actionType, category) => {
                    setPreselectedAction(actionType);
                    setPreselectedCategory(category);
                    setActionModalOpen(true);
                  }}
                />
                <ReviewRiskDropdown
                  disabled={selectAll.isFetching}
                  onSelectAction={(action) => {
                    setReviewRiskAction(action);
                    setReviewRiskModalOpen(true);
                  }}
                />
                {category !== "pendingManualReview" && category !== "manuallyReviewed" && (
                  <AddRiskModal selectedVideos={selected} onSuccess={handleClearSelection}>
                    <Button isDisabled={selectAll.isFetching} iconLeading={ShieldPlus}>
                      Add Risk
                    </Button>
                  </AddRiskModal>
                )}
              </div>
            </div>
          )}
        </div>

        <FilterPills className="-my-2" />

        <TableCard.Root className="bg-secondary_alt flex flex-col w-full flex-1" size="sm" rounded={false}>
          <Table
            aria-label="Video Explorer"
            selectionMode="multiple"
            selectedKeys={selected}
            onSelectionChange={handleSelectionChange}
            onRowAction={(key) => handleVideoIdChange(key as string)}
            sortDescriptor={descriptor}
            onSortChange={setFromDescriptor}
            className={cx((!data || data.videos.length === 0) && "h-full")}
          >
            <Table.Header className="sticky top-0 z-10 bg-secondary" allowSelectAll>
              <Table.Head id="videoTitle" label="Video Title" isRowHeader allowsSorting className="min-w-[280px]" />
              {showLatestAction && <Table.Head id="actions" label="Latest Status" className="px-2 min-w-[150px]" />}
              {!showLatestAction && (
                <Table.Head id="status" label="Safety Status" allowsSorting className="px-2 min-w-[150px]" />
              )}
              <Table.Head id="timeLastContacted" label="Time Last Contacted" className="px-2 min-w-[140px]" />
              <Table.Head id="flagCount" label="Risks" allowsSorting className="px-2 min-w-[80px]" />
              <Table.Head id="flagsLevel" label="Risk Levels" allowsSorting className="px-2 min-w-[120px]" />
              <Table.Head id="publishDate" label="Published" allowsSorting className="px-2 min-w-[100px]" />
              <Table.Head id="row-actions" label="" className="px-2 min-w-[60px]" />
            </Table.Header>

            <Table.Body
              items={data?.videos}
              className="text-xs bg-secondary_alt"
              renderEmptyState={() => (
                <UnboxTableEmptyStateContent
                  rows={isLoading ? undefined : data?.videos}
                  hasActiveFilters={hasActiveFilters}
                  emptyState={REVIEW_TABLE_EMPTY_STATES[category]}
                  filteredEmptyState={{ onClearFilters: handleClearFilters }}
                />
              )}
            >
              {(item) => (
                <Table.Row id={item.videoId} className="bg-primary cursor-pointer">
                  <Table.Cell>
                    <div className="flex flex-row items-center gap-2 min-w-0">
                      <div className="relative shrink-0">
                        {item.urlFirstFrame ? (
                          <img
                            src={item.urlFirstFrame}
                            alt={`${item.videoId} thumnail`}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100">
                            <span className="text-sm font-normal text-primary">
                              <Camera className="h-4 w-4" />
                            </span>
                          </div>
                        )}
                        {item.isInAdCampaign && <AdThumbnailBadge />}
                      </div>

                      <div className="flex items-leading flex-col min-w-0">
                        <span
                          className="text-sm font-medium text-primary truncate max-w-[200px] xl:max-w-[300px]"
                          title={item.videoTitle || ""}
                        >
                          {item.videoTitle}
                        </span>
                        <span className="text-xs font-medium text-gray-500 truncate max-w-[200px] xl:max-w-[300px]">
                          {item.creatorId ? formatCreatorId(item.creatorId) : "Unknown"}
                        </span>
                      </div>
                    </div>
                  </Table.Cell>

                  {showLatestAction && (
                    <Table.Cell className="whitespace-nowrap text-sm text-primary font-medium px-2">
                      {latestAction(item)}
                    </Table.Cell>
                  )}
                  {!showLatestAction && (
                    <Table.Cell className="whitespace-nowrap text-sm text-primary font-medium px-2">
                      <DetailedVideoStatusBadge status={item.status} />
                    </Table.Cell>
                  )}
                  <Table.Cell className="whitespace-nowrap text-sm text-tertiary font-medium px-2">
                    <TimeLastContactedBadge contactedAt={item.creatorContactedAt} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-sm text-tertiary font-medium px-2">
                    {videoFormatter.format(item.flagCount)}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-sm text-tertiary font-medium px-2">
                    <FlagBadges levels={item.flagTiers} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-sm text-tertiary font-medium px-2">
                    {item.publishDate}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-sm text-tertiary font-medium px-2">
                    <div className="flex flex-row gap-1 items-center justify-end">
                      {item.creatorId && (
                        <ButtonUtility
                          size="xs"
                          color="tertiary"
                          tooltip="Message Creator on TikTok"
                          icon={MessageTextSquare02}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (item.creatorId) setRowMessageCreatorId(item.creatorId);
                          }}
                        />
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="flex items-center justify-center w-8 h-8 hover:bg-secondary rounded transition-colors"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Video actions"
                          >
                            <DotsVertical className="w-5 h-5 text-tertiary" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          collisionPadding={16}
                          className="w-[280px] max-h-[min(70vh,500px)] overflow-x-hidden overflow-y-auto"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {category !== "pendingManualReview" && category !== "manuallyReviewed" && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setAddRiskVideoId(item.videoId);
                              }}
                            >
                              <ShieldPlus className="mr-2 size-4 text-fg-quaternary" />
                              <span className="text-secondary font-semibold">Add Risk</span>
                            </DropdownMenuItem>
                          )}
                          {item.creatorId && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                if (item.creatorId) setRowInviteCreatorId(item.creatorId);
                              }}
                            >
                              <Announcement03 className="mr-2 size-4 text-fg-quaternary" />
                              <span className="text-secondary font-semibold">Invite Creator to collaboration</span>
                            </DropdownMenuItem>
                          )}
                          {item.creatorId && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                if (item.creatorId) {
                                  setAddToListsCreatorId(item.creatorId);
                                }
                              }}
                            >
                              <FilePlus02 className="mr-2 size-4 text-fg-quaternary" />
                              <span className="text-secondary font-semibold">Add to List</span>
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
          <PaginationPageMinimalCenter
            page={pagination.page}
            total={Math.ceil((total ?? 0) / pagination.pagination.limit)}
            onPageChange={pagination.setPage}
            className="px-4 py-3 md:px-6 md:pt-2 md:pb-2 bg-primary"
          />
        </TableCard.Root>

        {selectedVideoId && (
          <VideoDetails
            isAssurance
            id={selectedVideoId}
            onClose={() => handleVideoIdChange(undefined)}
            defaultTab="safetyRisks"
          />
        )}

        <Dialog
          open={actionModalOpen}
          onOpenChange={(open) => {
            setActionModalOpen(open);
            if (!open) {
              setPreselectedAction(undefined);
              setPreselectedCategory(undefined);
            }
          }}
        >
          {actionModalOpen && (
            <ActionRecordDialog
              videos={Array.from(selectedVideosData.values())}
              selection={{ mode: "include", videoIds: selected }}
              selectedCount={selectedCount}
              onClose={() => {
                clearSelection();
                setActionModalOpen(false);
                setPreselectedAction(undefined);
                setPreselectedCategory(undefined);
              }}
              preselectedAction={preselectedAction}
              preselectedCategory={preselectedCategory}
            />
          )}
        </Dialog>

        <ReviewRiskModal
          open={reviewRiskModalOpen}
          onOpenChange={(open) => {
            setReviewRiskModalOpen(open);
            if (!open) {
              setReviewRiskAction(undefined);
            }
          }}
          selectedVideos={selected}
          initialAction={reviewRiskAction}
          onSuccess={handleClearSelection}
        />

        {addRiskVideoId && (
          <AddRiskVideoModal
            videoId={addRiskVideoId}
            existingRiskIds={[]}
            open
            onOpenChange={(open) => !open && setAddRiskVideoId(undefined)}
            onSuccess={() => setAddRiskVideoId(undefined)}
          />
        )}

        <RowInviteModalLoader creatorId={rowInviteCreatorId} onClose={() => setRowInviteCreatorId(undefined)} />

        <AddtoListsModal
          selectedCreators={addToListsCreatorId ? [addToListsCreatorId] : []}
          onAddToList={() => setAddToListsCreatorId(undefined)}
          open={addToListsCreatorId !== undefined}
          onOpenChange={(open) => {
            if (!open) setAddToListsCreatorId(undefined);
          }}
        />

        {rowMessageCreatorId && (
          <MessageCreatorsDialog
            recipients={[
              {
                creatorId: rowMessageCreatorId,
                handle: null,
                thumbnail: data?.videos.find((v) => v.creatorId === rowMessageCreatorId)?.urlFirstFrame ?? null,
              },
            ]}
            open={!!rowMessageCreatorId}
            onOpenChange={(open) => !open && setRowMessageCreatorId(undefined)}
            onSuccess={() => setRowMessageCreatorId(undefined)}
          />
        )}
      </div>

      <VideoFilterPanel open={filtersOpen} close={() => setFiltersOpen(false)} timePeriod="7d" />
    </div>
  );
};

const availableFilters: Record<SafetyVideoCategories, FilterField[]> = {
  pendingManualReview: [
    "publishDateStart",
    "publishDateEnd",
    "riskTier",
    "riskId",
    "creatorType",
    "creator",
    "hooks",
    "product",
    "tactics",
    "sellingPoints",
    "videoStyles",
    "videoLanguages",
    "campaign",
  ],
  manuallyReviewed: [
    "publishDateStart",
    "publishDateEnd",
    "riskTier",
    "riskId",
    "creatorType",
    "creator",
    "hooks",
    "product",
    "tactics",
    "sellingPoints",
    "videoStyles",
    "videoLanguages",
    "campaign",
  ],
  awaitingAction: [
    "publishDateStart",
    "publishDateEnd",
    "riskTier",
    "riskId",
    "creatorType",
    "creator",
    "hooks",
    "product",
    "tactics",
    "sellingPoints",
    "videoStyles",
    "videoLanguages",
    "campaign",
  ],
  actionInProgress: [
    "publishDateStart",
    "publishDateEnd",
    "riskTier",
    "riskId",
    "actionTaken",
    "creatorType",
    "creator",
    "hooks",
    "product",
    "tactics",
    "sellingPoints",
    "videoStyles",
    "videoLanguages",
    "campaign",
  ],
  loggedAsComplete: [
    "publishDateStart",
    "publishDateEnd",
    "riskTier",
    "riskId",
    "actionTaken",
    "creatorType",
    "creator",
    "hooks",
    "product",
    "tactics",
    "sellingPoints",
    "videoStyles",
    "videoLanguages",
    "campaign",
  ],
  risksDetected: [
    "publishDateStart",
    "publishDateEnd",
    "riskTier",
    "riskId",
    "creatorType",
    "creator",
    "hooks",
    "product",
    "tactics",
    "sellingPoints",
    "videoStyles",
    "videoLanguages",
    "campaign",
  ],
  deemedSafe: [
    "publishDateStart",
    "publishDateEnd",
    "creatorType",
    "creator",
    "hooks",
    "product",
    "tactics",
    "sellingPoints",
    "videoStyles",
    "videoLanguages",
    "campaign",
  ],
  unableToAnalyse: [
    "publishDateStart",
    "publishDateEnd",
    "creatorType",
    "creator",
    "hooks",
    "product",
    "tactics",
    "sellingPoints",
    "videoStyles",
    "videoLanguages",
    "campaign",
  ],
};

export const SafetyVideosTable = ({
  category,
  title,
  tabs,
  initialVideoId,
  onVideoIdChange,
  navigate,
  currentSearch,
}: SafetyVideosTableInnerProps) => {
  const activeFields = availableFilters[category];

  return (
    <FiltersProvider activeFields={activeFields} initialValues={currentSearch}>
      <SafetyVideosTableInner
        category={category}
        title={title}
        tabs={tabs}
        initialVideoId={initialVideoId}
        onVideoIdChange={onVideoIdChange}
        navigate={navigate}
        currentSearch={currentSearch}
      />
    </FiltersProvider>
  );
};

/**
 * Loads creator data (incl. tiktokCreatorOpenId) when the user opens the row-level invite modal,
 * since the row only carries the CID-prefixed creator id and the openId-only invite API needs
 * the actual TikTok open id. Renders nothing until the lookup resolves.
 */
const RowInviteModalLoader = ({ creatorId, onClose }: { creatorId: string | undefined; onClose: () => void }) => {
  const { data: creator } = useCreator(creatorId);
  if (!creatorId || !creator?.tiktokCreatorOpenId) return null;
  return (
    <InviteToCollaborationModal
      creators={[
        {
          openId: creator.tiktokCreatorOpenId,
          username: creator.creatorName ?? "",
          nickname: creator.nickname ?? null,
          avatar: creator.thumbnail ?? null,
        },
      ]}
      open={true}
      onOpenChange={(open) => !open && onClose()}
    />
  );
};
